import {BrsData, type ProjectData, type Inventory} from '@unbrs/plastic-data';
import ky from 'ky';

async function getProjectsFromUrl(url: string): Promise<ProjectData.IProject[]> {
	const result = ky.get(url).json<ProjectData.IProject[]>();

	return result;
}

type GetFeedOptions = {
	resourceUrl: string;
	type?: ProjectData.IFeedType;
	filter?: Record<ProjectData.IFeedType, string[]>;
};

export const getFeed = async (options: GetFeedOptions): Promise<ProjectData.IProject[]> => {
	const result: ProjectData.IProject[] = [];
	if (options.type) {
		switch (options.type) {
			case 'brs': {
				const brsData = await BrsData.getProjects();
				result.push(...brsData);
				break;
			}

			case 'gef': {
				const gefData = await getProjectsFromUrl(options.resourceUrl + 'gef.json');
				result.push(...gefData);
				break;
			}

			case 'undp': {
				const undpData = await getProjectsFromUrl(options.resourceUrl + 'undp.json');
				result.push(...undpData);
				break;
			}

			case 'iucn': {
				const iucnData = await getProjectsFromUrl(options.resourceUrl + 'iucn.json');
				result.push(...iucnData);
				break;
			}

			case 'unido': {
				const unidoData = await getProjectsFromUrl(options.resourceUrl + 'unido.json');
				result.push(...unidoData);
				break;
			}

			case 'worldbank': {
				const wbankData = await getProjectsFromUrl(options.resourceUrl + 'wbank.json');
				result.push(...wbankData);
				break;
			}

			default: {
				const manualData = await getProjectsFromUrl(options.resourceUrl + 'manual.json');
				result.push(...manualData);
				break;
			}
		}
	} else {
		// Fetch all projects when type is undefined
		const brsData = await BrsData.getProjects();
		const gefData = await getProjectsFromUrl(options.resourceUrl + 'gef.json');
		const undpData = await getProjectsFromUrl(options.resourceUrl + 'undp.json');
		const iucnData = await getProjectsFromUrl(options.resourceUrl + 'iucn.json');
		const unidoData = await getProjectsFromUrl(options.resourceUrl + 'unido.json');
		const wbankData = await getProjectsFromUrl(options.resourceUrl + 'wbank.json');
		const manualData = await getProjectsFromUrl(options.resourceUrl + 'manual.json');

		// Concatenate all projects
		result.push(...brsData, ...gefData, ...undpData, ...iucnData, ...unidoData, ...wbankData, ...manualData);
	}

	convertStringTypesToRightType(result);
	crossFeedProcessProjects(result);
	return result;
};

const convertStringTypesToRightType = (result: ProjectData.IProject[]): void => {
	for (const project of result) {
		project.startDate = project.startDate ? new Date(project.startDate) : undefined;
		project.endDate = project.endDate ? new Date(project.endDate) : undefined;
		project.sourceDate = new Date(project.sourceDate);
	}
};

const crossFeedProcessProjects = (projects: ProjectData.IProject[]): void => {
	// Process projects
	// Create a map of projects where the key is the project id
	const projectMap = new Map<string, ProjectData.IProject>();
	for (const project of projects) {
		projectMap.set(project.id, project);
	}

	// Set umbrellaUrl for each project
	for (const project of projects) {
		// Process umbrella projects
		if (project.umbrellaId) {
			const parent = projectMap.get(project.umbrellaId);
			if (parent) {
				parent.isUmbrella = true;
				project.umbrellaUrl = parent.url;
				project.umbrellaName = parent.name;
			} else if (!project.isUmbrella) {
				project.umbrellaId = undefined;
				console.log(`Programme project not found: ${project.umbrellaId} for project ${project.id} "${project.name}"`);
			}
		}
	}
};

export const getProject = async (id: string, resourceUrl: string): Promise<ProjectData.IProject | undefined> => {
	// Const feed: ProjectData.IFeedType = id.split('-')[0] as ProjectData.IFeedType;
	const projects = await getFeed({resourceUrl});
	return projects.find(project => project.id === id);
};

export const getInventories = async (resourceUrl: string): Promise<Inventory.InventoryData[]> => ky.get(resourceUrl + 'inventory.json').json<Inventory.InventoryData[]>();

export const getInventory = async (id: string, resourceUrl: string): Promise<Inventory.InventoryData | undefined> => {
	const inventories = await getInventories(resourceUrl);
	const ids = id.split('-');
	if (ids.length !== 2) {
		console.error(`Invalid inventory id: ${id}`);
		return undefined;
	}

	const iso2 = ids[0];
	const year = Number.parseInt(ids[1]!, 10);
	return inventories.find(inventory => inventory.iso2 === iso2 && inventory.year === year);
};
